import { ImportHtmlComponent } from '../import-html/import-html.component';
import { SimpleConfirmDialogComponent } from '../../../../dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { PackagingUnitNavigationService } from '../../../../../navigation/services/navigation-services/packaging-unit-navigation.service';
import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { ChangeType, ImportParentComponent } from '../import-parent/import-parent.component';
import { ChangePackagingUnitCandidateDto } from 'src/app/data-transfer/entities/packaging-unit-entities/change-packaging-unit-candidate-dto';
import { ImportPackagingUnitCandidateDto } from 'src/app/data-transfer/entities/packaging-unit-entities/import-packaging-unit-candidate-dto';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { DialogActions } from 'src/app/model/dictionary';
import { PackagingPart } from 'src/app/model/packaging-part-enum';

@Component({
  selector: 'app-import-packaging-unit',
  templateUrl: './import-packaging-unit.component.html',
  styleUrls: ['./import-packaging-unit.component.scss']
})
export class ImportPackagingUnitComponent extends ImportParentComponent implements OnInit {

  @ViewChild('importHtml') importHtmlComponent!: ImportHtmlComponent;
  @ViewChild('changeHtml') changeHtmlComponent!: ImportHtmlComponent;

  selectedCandidates: ImportPackagingUnitCandidateDto[] = [];
  selectedChanges: ChangePackagingUnitCandidateDto[] = [];

  displayedColumns: string[] = [
    'itemId', 'version' ,'exportingOrganization', 'packagingTypeName', 'brandName', 'productName', 'articleNumber', 'timestamp','action', 'select'
  ];

  constructor(
    protected dialog: MatDialog,
    protected route: ActivatedRoute,
    protected translateService: TranslateService,
    private importExportApiService: ImportExportApiService,
    private analysisApiService: AnalysisApiService,
    private packagingUnitNavigationService: PackagingUnitNavigationService,
    private spinner: NgxSpinnerService
  ) {
    super(dialog, route, translateService);
  }

  ngOnInit(): void {
    this.loadAllDataSources();
  }

  private loadAllDataSources() {
    this.spinner.show();
    const obsrevableImports = this.importExportApiService.getPackagingUnitImportCandidates();
    const obsrevableChanges = this.importExportApiService.getPackagingUnitTrackedChanges();

    this.loadDataSourceSubscription = forkJoin([obsrevableImports, obsrevableChanges]).subscribe(result => {
      this.dataSourceImports.data = result[0];
      this.importHtmlComponent.setDataSource(this.dataSourceImports);
      this.selectedCandidates = [];
      this.dataSourceChanges.data = result[1];
      this.changeHtmlComponent.setDataSource(this.dataSourceChanges);
      this.selectedChanges = [];
      this.spinner.hide();
    });
  }

  navigateToCandidate(candidate: ImportPackagingUnitCandidateDto) {
    this.packagingUnitNavigationService.navigateToPackagingUnitPreview(candidate.id);
  }

  navigateToChange(candidate: ChangePackagingUnitCandidateDto, seePackagingUnitBeforeChange: boolean) {
    // ugly crutch, but API requires PU ID for old PU and import candidate ID for new PU
    const id = seePackagingUnitBeforeChange ? candidate.existingPackagingUnit.id : candidate.importCandidateId;
    if (id == null) { return; }
    this.packagingUnitNavigationService.navigateToPackagingUnitPreview(id, true, seePackagingUnitBeforeChange);
  }

  // ---------- Import ----------

  setSelectedImportCandidates(importCandidates: ImportPackagingUnitCandidateDto[]) {
    this.selectedCandidates = importCandidates;
  }

  removeCandidates() {
    const deleteFunction = (candidateId: number) => this.importExportApiService.deletePackagingUnitImportCandidate(candidateId);
    super.removeCandidates(this.selectedCandidates, deleteFunction, this.importHtmlComponent);
  }

  async proceedWithImport() {
    await super.selectDirectory();
    if (this.selectedImportType == null || this.selectedCandidates.length < 1 || this.targetDirectory == null) { return; }
    this.spinner.show();
    let recPossibleForAll = true;
    let lcaPossibleForAll = true;
    const observablesRec = this.selectedCandidates.map(candidate =>
      this.analysisApiService.isRecyclabilityAnalysisPossibleImport(candidate.id, PackagingPart.Unit));

    let isRecPossible: boolean[] = [];
    if (observablesRec.length > 0) {
      isRecPossible = await firstValueFrom(forkJoin(observablesRec));
    }

    recPossibleForAll = !isRecPossible.includes(false);

    if (recPossibleForAll) {
      const observablesLca = this.selectedCandidates.map(candidate =>
        this.analysisApiService.isLifeCycleAnalysisPossibleImport(candidate.id, PackagingPart.Unit));

      let isLcaPossible: boolean[] = [];
      if (observablesLca.length > 0) {
        isLcaPossible = await firstValueFrom(forkJoin(observablesLca));
      }
      lcaPossibleForAll = !isLcaPossible.includes(false);
    }

    if (recPossibleForAll && lcaPossibleForAll) {
      this.doImport();
    } else {
      const data: SimpleDialogData = {
        title: this.translateService.instant('common.text.warning'),
        messages: [this.translateService.instant('warnings.importIncompletePackagingUnit')], icon: 'warning'
      };
      const confirmDialogConfig = getDialogConfig(data, '350px');
      const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, confirmDialogConfig);
      this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
        if (result.event === DialogActions.CONFIRM) {
          this.doImport();
        } else {
          this.spinner.hide();
          return;
        }
      });
    }
  }

  private async doImport() {
    const importFunction = (candidateId: number, targetDirId: number, isWithTracking: boolean) =>
      this.importExportApiService.importPackagingUnit(candidateId, targetDirId, isWithTracking);
    await super.importItems(this.selectedCandidates, importFunction);
    this.loadAllDataSources();
  }

  // ---------- Changes ----------

  setSelectedImportChanges(changes: ChangePackagingUnitCandidateDto[]) {
    this.selectedChanges = changes;
  }

  removeChanges() {
    const deleteFunction = (changeId: number) => this.importExportApiService.ignorePackagingUnitChanges(changeId);
    const idsToDelete: number[] = this.selectedChanges.map(x => x.existingPackagingUnit.id ?? -1).filter(x => x !== -1);
    super.removeChanges(this.selectedChanges, deleteFunction, idsToDelete, this.changeHtmlComponent);
  }

  async proceedWithChange() {
    let targetDirectoryRequired = false;
    if ([ChangeType.WithTrackingNew, ChangeType.WithoutTracking].includes(this.selectedChangeType)) {
      targetDirectoryRequired = true;
      await super.selectDirectory();
    }
    if (this.selectedChangeType == null || this.selectedChanges.length < 1 ||
      (this.targetDirectory == null && targetDirectoryRequired)) { return; }
    this.spinner.show();
    let recPossibleForAll = true;
    let lcaPossibleForAll = true;
    const observablesRec = this.selectedChanges.map(change =>
      this.analysisApiService.isRecyclabilityAnalysisPossibleImport(change.importCandidateId, PackagingPart.Unit));

    let isRecPossible: boolean[] = [];
    if (observablesRec.length > 0) {
      isRecPossible = await firstValueFrom(forkJoin(observablesRec));
    }
    recPossibleForAll = !isRecPossible.includes(false);

    if (recPossibleForAll) {
      const observablesLca = this.selectedChanges.map(change =>
        this.analysisApiService.isLifeCycleAnalysisPossibleImport(change.importCandidateId, PackagingPart.Unit));

      let isLcaPossible: boolean[] = [];
      if (observablesLca.length > 0) {
        isLcaPossible = await firstValueFrom(forkJoin(observablesLca));
      }
      lcaPossibleForAll = !isLcaPossible.includes(false);
    }

    if (recPossibleForAll && lcaPossibleForAll) {
      this.doChange();
    } else {
      const data: SimpleDialogData = {
        title: this.translateService.instant('common.text.warning'),
        messages: [this.translateService.instant('warnings.importIncompletePackagingUnit')], icon: 'warning'
      };
      const confirmDialogConfig = getDialogConfig(data, '350px');
      const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, confirmDialogConfig);
      this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
        if (result.event === DialogActions.CONFIRM) {
          this.doChange();
        } else {
          this.spinner.hide();
          return;
        }
      });
    }
  }

  private async doChange() {
    if (this.selectedChangeType === this.changeTypeEnum.WithTrackingOverwrite) {
      const changeFunction = (changeId: number) => this.importExportApiService.applyPackagingUnitChanges(changeId);
      const idsToChange: number[] = this.selectedChanges.map(x => x.existingPackagingUnit.id ?? -1).filter(x => x !== -1);
      await super.applyChangesExistingItems(idsToChange, changeFunction);
      this.selectedChanges = [];
    } else {
      const isWithTracking = this.selectedChangeType === this.changeTypeEnum.WithTrackingNew;
      const changeFunction = (candidateId: number, targetDirectoryId: number, isTracking: boolean) =>
        this.importExportApiService.importPackagingUnit(candidateId, targetDirectoryId, isTracking);
      const idsToChange: number[] = this.selectedChanges.map(x => x.importCandidateId);
      await super.applyChangesNewItems(idsToChange, isWithTracking, changeFunction);
      this.selectedChanges = [];
    }
    this.loadAllDataSources();
  }
}
